/**
 * The workspace's page tree (spec §8.25, M24): pages and folders, arranged by dragging exactly like
 * blocks on a page (§8.6 item 4) — the pointer's position over the target row decides before / into /
 * after, *into* only over a folder; rows do not shift while dragging. Each row has a menu with the
 * same moves. Folder collapse is per reader (IndexedDB meta). Not virtualised: a workspace has tens of
 * pages, so the rail beside it may measure the DOM (`data-page-row`).
 */
import { Link } from "@tanstack/react-router";
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent, type DragMoveEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, type SortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { clsx } from "clsx";
import type { TreeID } from "loro-crdt";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Copy, FilePlus, FileText, Folder, FolderInput, FolderOpen, FolderOutput, FolderPlus, GripVertical, Link2, ListPlus, Trash2, Ungroup } from "lucide-react";
import { forwardRef, useEffect, useMemo, useState, type ReactNode } from "react";
import type { PageInfo } from "../api/types";
import { dropPlacement, type DropPlacement } from "../blocks/dropZones";
import { LoroTextInput } from "../blocks/textField";
import { useT } from "../i18n";
import { docIri, folderIri } from "../lib/iri";
import type { DocHandle } from "../sync/docManager";
import { useLoroSelector } from "../sync/hooks";
import { getMeta, setMeta } from "../sync/persistence";
import { usePopover } from "../ui/usePopover";
import { createFolder, deleteEmptyFolder, dissolveFolder, isSelfOrDescendant, movePageNode, movePageNodeRelative, pagesTree, readPageRows, samePageRows, type PageRow } from "./pageTree";

const stillRows: SortingStrategy = () => null;
/** one empty list, so the memos below do not recompute while the document opens */
const NO_ROWS: PageRow[] = [];

export interface PageTreeProps {
  workspaceId: string;
  handle: DocHandle;
  pages: PageInfo[];
  canWrite: boolean;
  sequences: Array<{ id: string; label: string }>;
  /** open the links panel for a page (§8.25) */
  onLink(docId: string): void;
  onAddToSequence(seqId: string, iri: string): void;
  /** create a page under `parent` (a folder) or at the root */
  onNewPage(parent?: TreeID): void;
  /** rows the reader currently sees, for the rail's geometry and relations */
  onVisibleRows?(rows: PageRow[]): void;
  /** drawn beside the list (the workspace rail) */
  overlay?: ReactNode;
  gutters?: { left: number; right: number };
}

export const PageTree = forwardRef<HTMLUListElement, PageTreeProps>(function PageTree(
  { workspaceId, handle, pages, canWrite, sequences, onLink, onAddToSequence, onNewPage, onVisibleRows, overlay, gutters },
  listRef,
) {
  const t = useT();
  const doc = handle.doc;
  const all = useLoroSelector(doc, (d) => readPageRows(d), samePageRows) ?? NO_ROWS;
  const collapseKey = `folders-collapsed:${workspaceId}`;
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  useEffect(() => {
    let alive = true;
    void getMeta<string[]>(collapseKey).then((v) => alive && Array.isArray(v) && setCollapsed(new Set(v)));
    return () => {
      alive = false;
    };
  }, [collapseKey]);
  const toggleCollapse = (id: string) => {
    setCollapsed((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      void setMeta(collapseKey, [...next]);
      return next;
    });
  };
  // a collapsed folder hides everything below it (rows come in tree order, so a depth check suffices)
  const rows = useMemo(() => {
    const out: PageRow[] = [];
    let hideBelow: number | null = null;
    for (const r of all) {
      if (hideBelow !== null && r.depth > hideBelow) continue;
      hideBelow = r.kind === "folder" && collapsed.has(r.treeId) ? r.depth : null;
      out.push(r);
    }
    return out;
  }, [all, collapsed]);
  useEffect(() => onVisibleRows?.(rows), [rows, onVisibleRows]);
  const byId = useMemo(() => new Map(pages.map((p) => [p.id, p])), [pages]);
  const inTree = useMemo(() => new Set(all.filter((r) => r.kind === "page").map((r) => r.docId)), [all]);

  // drag and drop, the block tree's rules (§8.6 item 4)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const [dropHint, setDropHint] = useState<{ treeId: string; place: DropPlacement } | null>(null);
  const placementOf = (e: DragMoveEvent | DragEndEvent): { target: PageRow; place: DropPlacement } | null => {
    if (!canWrite || !e.over || e.active.id === e.over.id) return null;
    const target = rows.find((r) => r.treeId === e.over!.id);
    if (!target || isSelfOrDescendant(doc, e.active.id as TreeID, target.treeId)) return null;
    const start = e.activatorEvent as PointerEvent | MouseEvent;
    const y = (start?.clientY ?? 0) + e.delta.y;
    return { target, place: dropPlacement(y, e.over.rect, target.kind === "folder") };
  };
  const onDragMove = (e: DragMoveEvent) => {
    const p = placementOf(e);
    setDropHint((cur) => (p ? (cur?.treeId === p.target.treeId && cur.place === p.place ? cur : { treeId: p.target.treeId, place: p.place }) : null));
  };
  const onDragEnd = (e: DragEndEvent) => {
    setDropHint(null);
    const p = placementOf(e);
    if (!p) return;
    const active = e.active.id as TreeID;
    if (p.place === "into") {
      // into a folder: at its start, or at its end when it is collapsed (the reader cannot see the start)
      const isCollapsed = collapsed.has(p.target.treeId);
      movePageNode(doc, active, p.target.treeId, isCollapsed ? p.target.children : 0);
      if (isCollapsed) toggleCollapse(p.target.treeId);
    } else {
      movePageNodeRelative(doc, active, p.target.treeId, p.place);
    }
  };

  const siblings = (row: PageRow) => all.filter((r) => r.parent === row.parent);
  const sibling = (row: PageRow, delta: -1 | 1) => {
    const list = siblings(row);
    return list[list.findIndex((r) => r.treeId === row.treeId) + delta];
  };

  return (
    <div className="relative" style={gutters && (gutters.left || gutters.right) ? { marginLeft: gutters.left, marginRight: gutters.right } : undefined}>
      {overlay}
      <DndContext sensors={sensors} onDragMove={onDragMove} onDragEnd={onDragEnd} onDragCancel={() => setDropHint(null)}>
        <SortableContext items={rows.map((r) => r.treeId)} strategy={stillRows}>
          <ul ref={listRef} className="kss-surface divide-y divide-neutral-200 dark:divide-neutral-800" data-testid="page-tree">
            {rows.map((row) => (
              <SortableRow key={row.treeId} id={row.treeId} disabled={!canWrite}>
                {(drag, setRef, style) => (
                  <li
                    ref={setRef}
                    style={style}
                    className={clsx("group/row relative flex items-center gap-1.5 py-1.5 pr-2 has-[[data-menu-open]]:z-30", dropHint?.treeId === row.treeId && dropHint.place === "into" && "bg-accent-tint ring-2 ring-inset ring-accent")}
                    data-page-row={row.treeId}
                    data-kind={row.kind}
                    data-doc-id={row.docId}
                    data-depth={row.depth}
                    data-title={row.kind === "folder" ? row.title : undefined}
                    data-drop={dropHint?.treeId === row.treeId ? dropHint.place : undefined}
                  >
                    {(dropHint?.treeId === row.treeId && (dropHint.place === "before" || dropHint.place === "after")) && (
                      <div className={clsx("pointer-events-none absolute right-0 z-20 h-0.5 bg-accent", dropHint.place === "before" ? "top-0" : "bottom-0")} style={{ left: 16 + row.depth * 18 }} data-testid="page-drop-line" />
                    )}
                    <div className="flex shrink-0 items-center" style={{ paddingLeft: 4 + row.depth * 18 }}>
                      {canWrite ? (
                        <button className="cursor-grab rounded p-0.5 text-neutral-300 opacity-0 transition hover:text-neutral-600 focus:opacity-100 group-hover/row:opacity-100 dark:text-neutral-600" aria-label={row.kind === "folder" ? t("tree.dragFolder") : t("tree.dragPage")} {...drag} data-testid="page-drag-handle">
                          <GripVertical size={14} />
                        </button>
                      ) : (
                        <span className="w-[22px]" />
                      )}
                    </div>
                    {row.kind === "folder" ? (
                      <FolderRow row={row} handle={handle} canWrite={canWrite} collapsed={collapsed.has(row.treeId)} onToggle={() => toggleCollapse(row.treeId)} />
                    ) : (
                      <PageRowView row={row} page={byId.get(row.docId!)} workspaceId={workspaceId} />
                    )}
                    <RowMenu
                      row={row}
                      canWrite={canWrite}
                      sequences={sequences}
                      up={sibling(row, -1)}
                      down={sibling(row, 1)}
                      onLink={() => row.docId && onLink(row.docId)}
                      onAddToSequence={(seqId) => row.docId && onAddToSequence(seqId, docIri(row.docId))}
                      onMoveUp={() => movePageNodeRelative(doc, row.treeId, sibling(row, -1)!.treeId, "before")}
                      onMoveDown={() => movePageNodeRelative(doc, row.treeId, sibling(row, 1)!.treeId, "after")}
                      onIntoFolderAbove={() => {
                        const above = sibling(row, -1)!;
                        movePageNode(doc, row.treeId, above.treeId, above.children);
                      }}
                      onOutOfFolder={() => row.parent && movePageNodeRelative(doc, row.treeId, row.parent as TreeID, "after")}
                      onCopyIri={() => void navigator.clipboard.writeText(row.kind === "folder" ? folderIri(workspaceId, row.treeId) : docIri(row.docId!))}
                      onNewPage={() => onNewPage(row.treeId)}
                      onNewFolder={() => {
                        createFolder(doc, t("tree.newFolderTitle"), row.treeId, 0);
                        if (collapsed.has(row.treeId)) toggleCollapse(row.treeId);
                      }}
                      onDissolve={() => dissolveFolder(doc, row.treeId)}
                      onDelete={() => deleteEmptyFolder(doc, row.treeId)}
                    />
                  </li>
                )}
              </SortableRow>
            ))}
            {pages
              .filter((p) => !inTree.has(p.id))
              .map((p) => (
                <li key={p.id} className="px-4 py-2.5 text-neutral-500">
                  <Link to="/w/$workspaceId/d/$docId" params={{ workspaceId, docId: p.id }} className="underline">
                    {p.title || t("page.untitled")}
                  </Link>{" "}
                  <span className="text-xs">{t("ws.notInTree")}</span>
                </li>
              ))}
            {all.length === 0 && pages.length === 0 && <li className="px-4 py-6 text-center text-neutral-500">{t("ws.noPages")}</li>}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
});

function SortableRow({ id, disabled, children }: { id: string; disabled: boolean; children: (drag: Record<string, unknown>, setRef: (el: HTMLElement | null) => void, style: React.CSSProperties) => ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style: React.CSSProperties = { transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return <>{children({ ...attributes, ...listeners }, setNodeRef, style)}</>;
}

function PageRowView({ row, page, workspaceId }: { row: PageRow; page: PageInfo | undefined; workspaceId: string }) {
  const t = useT();
  return (
    <div className="flex min-w-0 grow items-center gap-2">
      <FileText size={14} className="shrink-0 text-neutral-400" aria-hidden="true" />
      <Link to="/w/$workspaceId/d/$docId" params={{ workspaceId, docId: row.docId! }} className="truncate font-medium text-neutral-900 hover:text-accent dark:text-neutral-100" data-testid="page-tree-link">
        {page?.title || t("page.untitled")}
      </Link>
      {page?.my_mode === "read" && <span className="text-xs text-neutral-500">{t("ws.readOnly")}</span>}
    </div>
  );
}

function FolderRow({ row, handle, canWrite, collapsed, onToggle }: { row: PageRow; handle: DocHandle; canWrite: boolean; collapsed: boolean; onToggle(): void }) {
  const t = useT();
  const node = pagesTree(handle.doc).getNodeByID(row.treeId);
  return (
    <div className="flex min-w-0 grow items-center gap-1.5" data-testid="folder-row">
      <button className="shrink-0 text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100" onClick={onToggle} aria-expanded={!collapsed} aria-label={collapsed ? t("tree.expand") : t("tree.collapse")} data-testid="folder-toggle">
        {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
      </button>
      {collapsed ? <Folder size={14} className="shrink-0 text-neutral-400" aria-hidden="true" /> : <FolderOpen size={14} className="shrink-0 text-neutral-400" aria-hidden="true" />}
      {node ? (
        <LoroTextInput handle={handle} map={node.data} field="title" placeholder={t("tree.folderTitle")} readOnly={!canWrite} className="min-w-0 grow bg-transparent font-semibold outline-none" testId="folder-title" />
      ) : (
        <span className="font-semibold">{row.title}</span>
      )}
      {collapsed && row.children > 0 && <span className="shrink-0 text-xs text-neutral-500" data-testid="folder-count">{t("tree.items", { count: row.children })}</span>}
    </div>
  );
}

function RowMenu(props: {
  row: PageRow;
  canWrite: boolean;
  sequences: Array<{ id: string; label: string }>;
  up?: PageRow;
  down?: PageRow;
  onLink(): void;
  onAddToSequence(seqId: string): void;
  onMoveUp(): void;
  onMoveDown(): void;
  onIntoFolderAbove(): void;
  onOutOfFolder(): void;
  onCopyIri(): void;
  onNewPage(): void;
  onNewFolder(): void;
  onDissolve(): void;
  onDelete(): void;
}) {
  const t = useT();
  const { row, canWrite } = props;
  const { open, toggle, close, buttonRef, panelRef } = usePopover();
  const [seqOpen, setSeqOpen] = useState(false);
  const run = (fn: () => void) => () => {
    close();
    fn();
  };
  const folder = row.kind === "folder";
  return (
    <div className="relative shrink-0">
      <button ref={buttonRef} className="rounded p-1 text-neutral-400 opacity-0 transition hover:bg-neutral-100 hover:text-neutral-900 focus:opacity-100 group-hover/row:opacity-100 aria-expanded:opacity-100 dark:hover:bg-neutral-800" aria-label={t("tree.menu")} aria-haspopup="menu" aria-expanded={open} onClick={toggle} data-testid="page-menu-button">
        ☰
      </button>
      {open && (
        <div ref={panelRef} className="absolute right-0 top-7 z-30 w-56 rounded-md border border-neutral-200 bg-[var(--surface)] p-1 text-sm shadow-[var(--shadow-md)] dark:border-neutral-700" data-menu-open="" data-testid="page-menu">
          {!folder && <Item icon={<Link2 size={14} />} label={canWrite ? t("block.menu.link") : t("block.menu.links")} onClick={run(props.onLink)} testId="page-menu-link" />}
          {!folder && <Item icon={<ListPlus size={14} />} label={t("block.menu.sequence")} disabled={!canWrite || props.sequences.length === 0} onClick={() => setSeqOpen((v) => !v)} testId="page-menu-sequence" />}
          {seqOpen &&
            props.sequences.map((s) => (
              <button key={s.id} className="block w-full truncate rounded px-6 py-1 text-left text-xs hover:bg-neutral-100 dark:hover:bg-neutral-800" onClick={run(() => props.onAddToSequence(s.id))} data-testid="page-menu-sequence-option">
                {s.label || t("block.menu.untitledSequence")}
              </button>
            ))}
          {folder && <Item icon={<FilePlus size={14} />} label={t("tree.newPageHere")} disabled={!canWrite} onClick={run(props.onNewPage)} testId="page-menu-new-page" />}
          {folder && <Item icon={<FolderPlus size={14} />} label={t("tree.newFolderHere")} disabled={!canWrite} onClick={run(props.onNewFolder)} testId="page-menu-new-folder" />}
          <Item icon={<ArrowUp size={14} />} label={t("block.menu.up")} disabled={!canWrite || !props.up} onClick={run(props.onMoveUp)} testId="page-menu-up" />
          <Item icon={<ArrowDown size={14} />} label={t("block.menu.down")} disabled={!canWrite || !props.down} onClick={run(props.onMoveDown)} testId="page-menu-down" />
          {canWrite && props.up?.kind === "folder" && <Item icon={<FolderInput size={14} />} label={t("tree.intoFolderAbove")} onClick={run(props.onIntoFolderAbove)} testId="page-menu-into-folder" />}
          {canWrite && row.parent && <Item icon={<FolderOutput size={14} />} label={t("tree.outOfFolder")} onClick={run(props.onOutOfFolder)} testId="page-menu-out-of-folder" />}
          <Item icon={<Copy size={14} />} label={folder ? t("tree.copyFolderIri") : t("tree.copyPageIri")} onClick={run(props.onCopyIri)} testId="page-menu-copy-iri" />
          {folder && row.children > 0 && <Item icon={<Ungroup size={14} />} label={t("tree.dissolve")} disabled={!canWrite} onClick={run(props.onDissolve)} testId="page-menu-dissolve" />}
          {folder && row.children === 0 && <Item icon={<Trash2 size={14} />} label={t("tree.deleteFolder")} disabled={!canWrite} onClick={run(props.onDelete)} danger testId="page-menu-delete-folder" />}
        </div>
      )}
    </div>
  );
}

function Item({ icon, label, onClick, disabled, danger, testId }: { icon: ReactNode; label: string; onClick?: () => void; disabled?: boolean; danger?: boolean; testId?: string }) {
  return (
    <button className={clsx("flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-neutral-100 disabled:opacity-40 dark:hover:bg-neutral-800", danger && "text-err")} onClick={onClick} disabled={disabled} data-testid={testId}>
      {icon} {label}
    </button>
  );
}
